<!-- shift-swap-request.component.html -->
<div class="container-fluid py-4">
  <!-- Toast -->
  <div
    *ngIf="toastVisible"
    class="position-fixed top-0 start-50 translate-middle-x mt-3"
    style="z-index: 9999"
  >
    <div
      class="alert alert-dismissible fade show shadow-lg"
      [ngClass]="{
        'alert-success': toastType === 'success',
        'alert-danger': toastType === 'error'
      }"
      role="alert"
    >
      <i
        class="me-2"
        [class.bi-check-circle-fill]="toastType === 'success'"
        [class.bi-exclamation-circle-fill]="toastType === 'error'"
      ></i>
      {{ toastMessage }}
      <button
        type="button"
        class="btn-close"
        (click)="toastVisible = false"
        aria-label="Close"
      ></button>
    </div>
  </div>

  <!-- Header -->
  <div class="card bg-linear-gradient-purple text-white mb-4 p-3 p-md-4">
    <div class="d-flex justify-content-between align-items-center">
      <div>
        <h2 class="mb-1 fs-4">
          <i class="bi bi-arrow-left-right me-2"></i>Shift Swap Requests
        </h2>
        <p class="mb-0 opacity-75 small">
          Request to swap shifts with coworkers
        </p>
      </div>
      <button class="btn btn-outline-light btn-sm" (click)="goBack()">
        <i class="bi bi-arrow-left me-1"></i>Back
      </button>
    </div>
  </div>

  <!-- View Toggle -->
  <div class="btn-group w-100 mb-4 shadow-sm" role="group">
    <button
      type="button"
      class="btn"
      [class.btn-gradient]="viewMode === 'create'"
      [class.btn-outline-secondary]="viewMode !== 'create'"
      (click)="switchView('create')"
    >
      <i class="bi bi-plus-circle me-2"></i>New Request
    </button>
    <button
      type="button"
      class="btn position-relative"
      [class.btn-gradient]="viewMode === 'my-requests'"
      [class.btn-outline-secondary]="viewMode !== 'my-requests'"
      (click)="switchView('my-requests')"
    >
      <i class="bi bi-list-ul me-2"></i>My Requests
      <span
        *ngIf="mySwapRequests.length > 0"
        class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
      >
        {{ mySwapRequests.length }}
      </span>
    </button>
    <button
      type="button"
      class="btn position-relative"
      [class.btn-gradient]="viewMode === 'pending-approval'"
      [class.btn-outline-secondary]="viewMode !== 'pending-approval'"
      (click)="switchView('pending-approval')"
    >
      <i class="bi bi-hourglass-split me-2"></i>Pending Your Approval
      <span
        *ngIf="
          pendingApprovals.length > 0 ||
          (isManager && managerPendingApprovals.length > 0)
        "
        class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-warning text-dark"
      >
        {{
          pendingApprovals.length +
            (isManager ? managerPendingApprovals.length : 0)
        }}
      </span>
    </button>
  </div>

  <!-- CREATE NEW REQUEST VIEW -->
  <div *ngIf="viewMode === 'create'">
    <!-- Step Progress -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div
            class="d-flex align-items-center"
            [class.text-primary]="currentStep >= 1"
          >
            <div
              class="rounded-circle d-flex align-items-center justify-content-center me-2"
              [class.bg-primary]="currentStep >= 1"
              [class.bg-secondary]="currentStep < 1"
              [class.text-white]="currentStep >= 1"
              style="width: 2rem; height: 2rem"
            >
              1
            </div>
            <span class="small fw-semibold d-none d-lg-inline">Your Shift</span>
          </div>

          <div class="flex-grow-1 border-top mx-2"></div>

          <div
            class="d-flex align-items-center"
            [class.text-primary]="currentStep >= 2"
          >
            <div
              class="rounded-circle d-flex align-items-center justify-content-center me-2"
              [class.bg-primary]="currentStep >= 2"
              [class.bg-secondary]="currentStep < 2"
              [class.text-white]="currentStep >= 2"
              style="width: 2rem; height: 2rem"
            >
              2
            </div>
            <span class="small fw-semibold d-none d-lg-inline"
              >Desired Shift</span
            >
          </div>

          <div class="flex-grow-1 border-top mx-2"></div>

          <div
            class="d-flex align-items-center"
            [class.text-primary]="currentStep >= 3"
          >
            <div
              class="rounded-circle d-flex align-items-center justify-content-center me-2"
              [class.bg-primary]="currentStep >= 3"
              [class.bg-secondary]="currentStep < 3"
              [class.text-white]="currentStep >= 3"
              style="width: 2rem; height: 2rem"
            >
              3
            </div>
            <span class="small fw-semibold d-none d-lg-inline">Submit</span>
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 1: Select Your Shift -->
    <div *ngIf="currentStep === 1">
      <h5 class="mb-3">
        <i class="bi bi-1-circle me-2"></i>Select Your Shift to Swap
      </h5>

      <div class="alert alert-info mb-3">
        <i class="bi bi-info-circle me-2"></i>
        <strong>Tip:</strong> You can swap a working shift for someone's day
        off, or swap your day off for someone's working shift!
      </div>

      <div *ngIf="isLoading" class="text-center py-5">
        <div class="spinner-border text-primary"></div>
        <p class="mt-3 text-muted">Loading schedules...</p>
      </div>

      <div
        *ngIf="!isLoading && publishedSchedules.length === 0"
        class="alert alert-info"
      >
        <i class="bi bi-info-circle me-2"></i>No published schedules available
      </div>

      <div *ngFor="let schedule of publishedSchedules" class="card mb-3">
        <div class="card-header bg-light">
          <h6 class="mb-0">
            <i class="bi bi-calendar-week me-2"></i
            >{{ formatWeekLabel(schedule) }}
          </h6>
        </div>
        <div class="card-body">
          <div class="row g-2">
            <div
              *ngFor="let day of getMyShiftsFromSchedule(schedule)"
              class="col-md-6 col-lg-4"
            >
              <div
                class="card h-100 shift-card"

                role="button"
                (click)="
                  selectMyShift(schedule, day.dayKey, day.shift, day.date)
                "
              >
                <div class="card-body">
                  <h6 class="mb-1">{{ day.dayName }}</h6>
                  <p class="mb-0 small text-muted">{{ day.date }}</p>
                  <p
                    class="mb-0 fw-bold"
                    [ngClass]="day.isOff ? 'text-success' : 'text-primary'"
                  >
                    <i
                      class="me-1"
                      [ngClass]="day.isOff ? 'bi bi-calendar-x' : 'bi bi-clock'"
                    ></i>
                    {{ day.shift }}
                    
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div
            *ngIf="getMyShiftsFromSchedule(schedule).length === 0"
            class="text-muted small"
          >
            <i class="bi bi-info-circle me-1"></i>No shifts or days available
            for you this week
          </div>
        </div>
      </div>
    </div>

    <!-- STEP 2: Select Desired Shift (UPDATED - Mobile First with Filters) -->
    <div *ngIf="currentStep === 2">
      <div class="alert alert-success mb-3">
        <i class="bi bi-check-circle me-2"></i>
        <strong>Selected:</strong> Your {{ myShift?.dayKey }} shift ({{
          myShift?.shift
        }}) on {{ myShift?.date }}
      </div>

      <h5 class="mb-3">
        <i class="bi bi-2-circle me-2"></i>Select Shift You Want
      </h5>

      <!-- Filters (Mobile First) -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row g-2">
            <!-- Day Filter -->
            <div class="col-12 col-sm-6">
              <label class="form-label small fw-semibold mb-1">
                <i class="bi bi-calendar-day me-1"></i>Filter by Day
              </label>
              <select
                class="form-select form-select-sm"
                [(ngModel)]="selectedDayFilter"
              >
                <option value="all">All Days</option>
                <option
                  *ngFor="let day of dayKeys; let i = index"
                  [value]="day"
                >
                  {{ dayNames[i] }}
                </option>
              </select>
            </div>

            <!-- Employee Filter -->
            <div class="col-12 col-sm-6">
              <label class="form-label small fw-semibold mb-1">
                <i class="bi bi-person me-1"></i>Filter by Employee
              </label>
              <select
                class="form-select form-select-sm"
                [(ngModel)]="selectedEmployeeFilter"
              >
                <option value="all">All Employees</option>
                <option *ngFor="let emp of availableEmployees" [value]="emp">
                  {{ emp }}
                </option>
              </select>
            </div>

            <!-- Clear Filters Button -->
            <div
              class="col-12"
              *ngIf="
                selectedDayFilter !== 'all' || selectedEmployeeFilter !== 'all'
              "
            >
              <button
                class="btn btn-outline-secondary btn-sm w-100"
                (click)="resetFilters()"
              >
                <i class="bi bi-x-circle me-1"></i>Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Filtered Shifts (Mobile First Grid) -->
      <div class="row g-2 mb-3">
        <div
          *ngFor="let shift of getFilteredShifts()"
          class="col-12 col-sm-6 col-md-4 col-lg-3"
        >
          <div
            class="card h-100 shift-card"
            [ngClass]="{ 'border-success': shift.isOff }"
            role="button"
            (click)="selectDesiredShiftFromFiltered(shift)"
          >
            <div class="card-body p-3">
              <!-- Mobile: Stack info vertically -->
              <div
                class="d-flex justify-content-between align-items-start mb-2"
              >
                <h6 class="mb-0 fw-bold">{{ shift.dayName }}</h6>
                <span class="badge bg-primary small">{{
                  shift.employeeName
                }}</span>
              </div>

              <p class="mb-1 small text-muted">
                <i class="bi bi-calendar-event me-1"></i
                >{{ shift.date | date : "MMM d" }}
              </p>

              <!-- ✅ NEW: Show different styling for off days -->
              <p
                class="mb-1 fw-semibold"
                [ngClass]="shift.isOff ? 'text-success' : 'text-primary'"
              >
                <i
                  class="me-1"
                  [ngClass]="shift.isOff ? 'bi bi-calendar-x' : 'bi bi-clock'"
                ></i>
                {{ shift.shift }}
                <span *ngIf="shift.isOff" class="badge bg-success ms-1"
                  >Day Off</span
                >
              </p>

              <p class="mb-0 small text-muted">
                {{ shift.scheduleName }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="getFilteredShifts().length === 0" class="text-center py-5">
        <i class="bi bi-inbox display-1 text-muted opacity-25"></i>
        <h6 class="mt-3 text-muted">No available shifts found</h6>
        <p class="text-muted small mb-0">
          Try adjusting your filters or check back later
        </p>
      </div>

      <!-- Navigation -->
      <div class="d-grid gap-2 d-sm-flex">
        <button class="btn btn-outline-secondary" (click)="goBack()">
          <i class="bi bi-arrow-left me-1"></i>Back
        </button>
      </div>
    </div>

    <!-- STEP 3: Review & Submit -->
    <div *ngIf="currentStep === 3">
      <h5 class="mb-3"><i class="bi bi-3-circle me-2"></i>Review & Submit</h5>

      <div class="card mb-3">
        <div class="card-body">
          <h6 class="fw-bold mb-3">Swap Summary:</h6>

          <div class="row">
            <div class="col-md-6 mb-3">
              <div class="border rounded p-3 bg-light">
                <small class="text-muted d-block mb-1">You Give:</small>
                <h6 class="mb-1">
                  {{ myShift?.dayKey }} - {{ myShift?.shift }}
                </h6>
                <p class="mb-0 small text-muted">{{ myShift?.date }}</p>
                <p class="mb-0 small text-muted">{{ myShift?.scheduleName }}</p>
              </div>
            </div>

            <div class="col-md-6 mb-3">
              <div class="border rounded p-3 bg-light">
                <small class="text-muted d-block mb-1">You Take:</small>
                <h6 class="mb-1">
                  {{ desiredShift?.dayKey }} - {{ desiredShift?.shift }}
                </h6>
                <p class="mb-0 small text-muted">{{ desiredShift?.date }}</p>
                <p class="mb-0 small text-muted">
                  {{ desiredShift?.scheduleName }}
                </p>
                <span class="badge bg-primary mt-1"
                  >From: {{ desiredShift?.employeeName }}</span
                >
              </div>
            </div>
          </div>

          <form [formGroup]="swapRequestForm">
            <div class="mb-3">
              <label class="form-label fw-semibold">
                <i class="bi bi-chat-text me-2"></i>Notes (Optional)
              </label>
              <textarea
                class="form-control"
                formControlName="notes"
                rows="3"
                placeholder="Add any additional notes or reasons for this swap..."
              ></textarea>
              <small class="text-muted">Max 500 characters</small>
            </div>
          </form>

          <div class="alert alert-info">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Note:</strong> This request requires approval from
            <strong>{{ desiredShift?.employeeName }}</strong> and a manager.
          </div>

          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary" (click)="goBack()">
              <i class="bi bi-arrow-left me-1"></i>Back
            </button>
            <button
              class="btn btn-gradient flex-grow-1"
              (click)="submitSwapRequest()"
              [disabled]="isSubmitting"
            >
              <span *ngIf="!isSubmitting">
                <i class="bi bi-send me-2"></i>Submit Request
              </span>
              <span *ngIf="isSubmitting">
                <span class="spinner-border spinner-border-sm me-2"></span
                >Submitting...
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- MY REQUESTS VIEW -->
  <div *ngIf="viewMode === 'my-requests'">
    <h5 class="mb-3"><i class="bi bi-list-ul me-2"></i>My Swap Requests</h5>

    <div *ngIf="mySwapRequests.length === 0" class="text-center py-5">
      <i class="bi bi-inbox display-1 text-muted opacity-25"></i>
      <h6 class="mt-3 text-muted">No swap requests yet</h6>
      <button class="btn btn-gradient mt-2" (click)="switchView('create')">
        <i class="bi bi-plus-circle me-2"></i>Create New Request
      </button>
    </div>

    <div *ngFor="let request of mySwapRequests" class="card mb-3">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-start mb-3">
          <h6 class="mb-0">Swap Request</h6>
          <span class="badge" [ngClass]="getStatusBadgeClass(request.status)">
            {{ getStatusText(request.status) }}
          </span>
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <small class="text-muted d-block mb-1">You Give:</small>
            <div class="border rounded p-2 bg-light">
              <p class="mb-0 small">
                <strong>{{ request.shift1.dayKey }}</strong> -
                {{ request.shift1.originalShift }}
              </p>
              <p class="mb-0 small text-muted">{{ request.shift1.date }}</p>
            </div>
          </div>
          <div class="col-md-6">
            <small class="text-muted d-block mb-1">You Take:</small>
            <div class="border rounded p-2 bg-light">
              <p class="mb-0 small">
                <strong>{{ request.shift2.dayKey }}</strong> -
                {{ request.shift2.originalShift }}
              </p>
              <p class="mb-0 small text-muted">{{ request.shift2.date }}</p>
              <span class="badge bg-primary small"
                >From: {{ request.shift2.employeeName }}</span
              >
            </div>
          </div>
        </div>

        <div *ngIf="request.notes" class="mb-2">
          <small class="text-muted"
            ><i class="bi bi-chat-text me-1"></i>Notes:</small
          >
          <p class="mb-0 small">{{ request.notes }}</p>
        </div>

        <div class="d-flex justify-content-between align-items-center">
          <small class="text-muted">
            <i class="bi bi-clock me-1"></i>
            Submitted {{ request.createdAt?.toDate() | date : "short" }}
          </small>

          <div *ngIf="request.status === 'declined'">
            <small class="text-danger">
              <i class="bi bi-x-circle me-1"></i>
              Declined by {{ request.declinedBy }}
              <span *ngIf="request.declinedReason"
                >: {{ request.declinedReason }}</span
              >
            </small>
          </div>
        </div>

        <div *ngIf="request.status === 'approved'" class="mt-2">
          <div class="alert alert-success mb-0 small">
            <i class="bi bi-check-circle-fill me-1"></i>
            Approved by {{ request.managerApprovedBy }} on
            {{ request.managerApprovedAt?.toDate() | date : "short" }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- PENDING APPROVAL VIEW -->
  <div *ngIf="viewMode === 'pending-approval'">
    <!-- Employee Approvals -->
    <div *ngIf="pendingApprovals.length > 0" class="mb-4">
      <h5 class="mb-3">
        <i class="bi bi-person-check me-2"></i>Waiting for Your Approval
      </h5>

      <div
        *ngFor="let request of pendingApprovals"
        class="card mb-3 border-warning"
      >
        <div class="card-body">
          <div class="alert alert-warning mb-3">
            <i class="bi bi-exclamation-triangle me-2"></i>
            <strong>{{ request.requestedBy }}</strong> wants to swap shifts with
            you
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <small class="text-muted d-block mb-1">They Give You:</small>
              <div class="border rounded p-2 bg-light">
                <p class="mb-0 small">
                  <strong>{{ request.shift1.dayKey }}</strong> -
                  {{ request.shift1.originalShift }}
                </p>
                <p class="mb-0 small text-muted">{{ request.shift1.date }}</p>
              </div>
            </div>
            <div class="col-md-6">
              <small class="text-muted d-block mb-1">You Give Them:</small>
              <div class="border rounded p-2 bg-light">
                <p class="mb-0 small">
                  <strong>{{ request.shift2.dayKey }}</strong> -
                  {{ request.shift2.originalShift }}
                </p>
                <p class="mb-0 small text-muted">{{ request.shift2.date }}</p>
              </div>
            </div>
          </div>

          <div *ngIf="request.notes" class="mb-3">
            <small class="text-muted"
              ><i class="bi bi-chat-text me-1"></i>Their notes:</small
            >
            <p class="mb-0 small border-start border-3 border-info ps-2">
              {{ request.notes }}
            </p>
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-success flex-grow-1"
              (click)="approveAsEmployee(request)"
            >
              <i class="bi bi-check-circle me-1"></i>Approve Swap
            </button>
            <button
              class="btn btn-danger flex-grow-1"
              data-bs-toggle="modal"
              data-bs-target="#declineModal"
              (click)="openDeclineModal(request)"
            >
              <i class="bi bi-x-circle me-1"></i>Decline
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Manager Approvals -->
    <div *ngIf="isManager && managerPendingApprovals.length > 0">
      <h5 class="mb-3">
        <i class="bi bi-shield-check me-2"></i>Manager Approval Required
      </h5>

      <div
        *ngFor="let request of managerPendingApprovals"
        class="card mb-3 border-info"
      >
        <div class="card-body">
          <div class="alert alert-info mb-3">
            <p class="mb-2 mt-1">
              <i class="bi bi-info-circle me-2"></i>
              <strong>{{ request.requestedBy }}</strong> and
              <strong>{{ request.shift2.employeeName }}</strong> have agreed to
              swap shifts.
            </p>
            <p class="mb-0 small">
              <i class="bi bi-calendar-check me-1"></i>
              Upon approval, the published schedule will be automatically
              updated with the new shift assignments.
            </p>
          </div>

          <div class="row mb-3">
            <div class="col-md-6">
              <small class="text-muted d-block mb-1"
                >{{ request.requestedBy }}'s Shift:</small
              >
              <div class="border rounded p-2 bg-light">
                <p class="mb-0 small">
                  <strong>{{ request.shift1.dayKey }}</strong> -
                  {{ request.shift1.originalShift }}
                </p>
                <p class="mb-0 small text-muted">{{ request.shift1.date }}</p>
              </div>
            </div>
            <div class="col-md-6">
              <small class="text-muted d-block mb-1"
                >{{ request.shift2.employeeName }}'s Shift:</small
              >
              <div class="border rounded p-2 bg-light">
                <p class="mb-0 small">
                  <strong>{{ request.shift2.dayKey }}</strong> -
                  {{ request.shift2.originalShift }}
                </p>
                <p class="mb-0 small text-muted">{{ request.shift2.date }}</p>
              </div>
            </div>
          </div>

          <div *ngIf="request.notes" class="mb-3">
            <small class="text-muted"
              ><i class="bi bi-chat-text me-1"></i>Notes:</small
            >
            <p class="mb-0 small border-start border-3 border-info ps-2">
              {{ request.notes }}
            </p>
          </div>

          <div class="d-flex gap-2">
            <button
              class="btn btn-success flex-grow-1"
              (click)="approveAsManager(request)"
            >
              <i class="bi bi-check-circle me-1"></i>Approve as Manager
            </button>
            <button
              class="btn btn-danger flex-grow-1"
              data-bs-toggle="modal"
              data-bs-target="#declineModal"
              (click)="openDeclineModal(request)"
            >
              <i class="bi bi-x-circle me-1"></i>Decline
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- No Pending -->
    <div
      *ngIf="
        pendingApprovals.length === 0 &&
        (!isManager || managerPendingApprovals.length === 0)
      "
      class="text-center py-5"
    >
      <i class="bi bi-check-circle display-1 text-success opacity-25"></i>
      <h6 class="mt-3 text-muted">No pending approvals</h6>
    </div>
  </div>
</div>

<!-- Decline Modal -->
<div
  class="modal fade"
  id="declineModal"
  tabindex="-1"
  aria-labelledby="declineModalLabel"
  aria-hidden="true"
>
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header bg-danger text-white">
        <h5 class="modal-title" id="declineModalLabel">
          <i class="bi bi-x-circle me-2"></i>Decline Swap Request
        </h5>
        <button
          type="button"
          class="btn-close btn-close-white"
          data-bs-dismiss="modal"
        ></button>
      </div>
      <form [formGroup]="declineForm" (ngSubmit)="declineSwapRequest()">
        <div class="modal-body">
          <p class="text-muted">
            Please provide a reason for declining this swap request:
          </p>
          <textarea
            class="form-control"
            formControlName="reason"
            rows="3"
            placeholder="Enter reason for declining..."
            required
          ></textarea>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            data-bs-dismiss="modal"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-danger"
            [disabled]="declineForm.invalid"
            data-bs-dismiss="modal"
          >
            <i class="bi bi-x-circle me-1"></i>Decline Request
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
